/*
  <kg-hero-slider>: Punkte, Pfeile, Pause und Fortschrittsbalken fuer den Header-Slider.

  Gewischt wird ueber Scroll-Snap im CSS, das macht der Browser. Dieses Skript liest nur
  ab, welche Folie gerade steht, und schiebt beim Weiterschalten. Ohne das Skript bleibt
  der Bereich wischbar, nur ohne Punkte und ohne Autoplay.

  Der Wechsel haengt an einer Uhr, nicht am Balken: Der Balken ist nur die Anzeige und
  laesst sich abschalten, ohne dass der Slider stehen bleibt. Beim Anhalten merkt sich die
  Uhr die Restzeit, damit Balken und Wechsel nach dem Weiterlaufen gleich stehen.

  Losgezaehlt wird erst, wenn mindestens die Haelfte des Sliders im Bild steht. Liegt er
  weiter unten auf der Seite, laeuft er also nicht ins Leere, waehrend niemand hinsieht.
  Angehalten wird aber erst, wenn er ganz aus dem Bild ist — dazwischen laeuft er weiter,
  sonst stockte er bei jedem kleinen Scrollen.

  Angehalten wird beim Tastaturfokus, wenn die Seite im Hintergrund liegt, wenn der
  Slider wieder aus dem Bild scrollt und wenn im System "Bewegung reduzieren" steht.
  Bewusst nicht beim Zeigen mit der Maus: Der Slider ist randlos und fuellt oft den
  ganzen Schirm, der Zeiger liegt also fast immer darauf und er stuende nie weiter.
*/

class KgHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.folien = [...this.querySelectorAll('.hero-slider__slide')];
    this.punkte = [...this.querySelectorAll('[data-hero-go]')];
    this.knopf = this.querySelector('[data-hero-pause]');
    this.fuellung = this.querySelector('[data-hero-fill]');
    if (!this.track || this.folien.length < 2) return;

    this.dauer = Number(this.dataset.interval) || 0;
    this.rest = this.dauer;
    this.index = 0;
    this.vonHand = false;
    this.beruehrt = false;
    this.sichtbar = false;
    this.laeuft = false;
    this.ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.punkte.forEach((punkt) => {
      punkt.addEventListener('click', () => this.zeigen(Number(punkt.dataset.heroGo)));
    });
    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.zeigen(this.index - 1));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.zeigen(this.index + 1));
    this.knopf?.addEventListener('click', () => this.umschalten());

    /* Beim Wischen wandert nur der Zeiger mit, angestossen wird nichts */
    this.track.addEventListener('scroll', () => this.abgleichen(), { passive: true });

    this.addEventListener('focusin', () => this.beruehren(true));
    this.addEventListener('focusout', () => this.beruehren(false));
    document.addEventListener('visibilitychange', () => this.pruefen());
    this.ruhig.addEventListener('change', () => this.pruefen());

    /*
      Zwei verschiedene Schwellen mit Absicht: angehen ab der halben Flaeche, ausgehen
      erst bei ganz verschwunden. Mit einer Schwelle fuer beides wuerde er genau an der
      Grenze staendig an- und ausgehen. Der Zusatz fuer hohe Folien: Ist der Slider
      hoeher als der Schirm, waere die Haelfte nie erreichbar — dann genuegt es, dass er
      den Schirm weitgehend fuellt.
    */
    this.beobachter = new IntersectionObserver(
      (eintraege) => {
        const eintrag = eintraege[0];
        const genug =
          eintrag.intersectionRatio >= 0.5 ||
          eintrag.intersectionRect.height >= window.innerHeight * 0.8;
        if (genug) {
          this.sichtbar = true;
        } else if (!eintrag.isIntersecting) {
          this.sichtbar = false;
        }
        this.pruefen();
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    this.beobachter.observe(this);
  }

  disconnectedCallback() {
    this.beobachter?.disconnect();
    window.clearTimeout(this.uhr);
  }

  /* Welche Folie steht gerade? Ergibt sich aus der Scrollposition. */
  abgleichen() {
    const breite = this.track.clientWidth;
    if (!breite) return;
    const neu = Math.round(this.track.scrollLeft / breite);
    if (neu === this.index) return;
    this.index = neu;
    this.punkte.forEach((punkt, i) => {
      if (i === neu) {
        punkt.setAttribute('aria-current', 'true');
      } else {
        punkt.removeAttribute('aria-current');
      }
    });
    this.rest = this.dauer;
    this.neuZeichnen();
  }

  /*
    Was von Hand kommt, gleitet immer: Wer auf einen Punkt tippt, will die Bewegung sehen.
    Nur das Autoplay schneidet hart, und auch nur, wenn es weiter als zwei Folien zurueck
    muesste — sonst rauschte ein langer Slider am Ende sichtbar durch alle Folien.
  */
  zeigen(ziel, vomAutoplay) {
    const anzahl = this.folien.length;
    const index = ((ziel % anzahl) + anzahl) % anzahl;
    const weite = Math.abs(index - this.index);
    const schnitt = vomAutoplay && weite > 2;
    const sanft = !schnitt && !this.ruhig.matches;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: sanft ? 'smooth' : 'auto' });
  }

  umschalten() {
    this.vonHand = !this.vonHand;
    const label = this.vonHand ? this.knopf.dataset.labelPlay : this.knopf.dataset.labelPause;
    this.knopf.setAttribute('aria-label', label);
    this.knopf.querySelector('[data-hero-icon-pause]').toggleAttribute('hidden', this.vonHand);
    this.knopf.querySelector('[data-hero-icon-play]').toggleAttribute('hidden', !this.vonHand);
    if (this.vonHand) this.rest = this.dauer;
    this.pruefen();
  }

  beruehren(ja) {
    this.beruehrt = ja;
    this.pruefen();
  }

  /* Eine Stelle entscheidet, ob die Uhr laeuft. Sonst widersprechen sich die Anlaesse. */
  pruefen() {
    const darf = this.dauer > 0 && this.sichtbar && !this.vonHand && !document.hidden && !this.ruhig.matches;
    const soll = darf && !this.beruehrt;
    this.toggleAttribute('data-running', darf);
    this.toggleAttribute('data-paused', darf && this.beruehrt);

    if (soll === this.laeuft) return;
    this.laeuft = soll;
    if (soll) {
      this.start = Date.now();
      this.uhr = window.setTimeout(() => this.zeigen(this.index + 1, true), this.rest);
    } else {
      window.clearTimeout(this.uhr);
      /* Restzeit merken, damit der Balken nach dem Weiterlaufen an derselben Stelle steht */
      if (this.start) this.rest = Math.max(0, this.rest - (Date.now() - this.start));
    }
  }

  /* Die Animation des Balkens faengt nur neu an, wenn sie kurz entfernt und wieder gesetzt wird */
  neuZeichnen() {
    window.clearTimeout(this.uhr);
    this.laeuft = false;
    this.removeAttribute('data-running');
    if (this.fuellung) void this.fuellung.offsetWidth;
    this.pruefen();
  }
}

customElements.define('kg-hero-slider', KgHeroSlider);
